import { domainError, type DomainError } from '../shared/domain-error';
import type { PileCapTypeId, ProjectId } from '../shared/ids';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import { checkTypeLines, type ItemFactor } from './own-factor';
import type { SteelRate } from './steel';

/**
 * O tipo de bloco (Fase 41, etapa 1 — F1, F9; RN-536, RN-537).
 *
 * "BL2E", "BL3E": o bloco de duas estacas leva outro concreto por m³ que o de três, e escrever o
 * fator bloco a bloco seria repetir o mesmo número em quarenta cards. O tipo guarda a
 * **composição** — o fator de cada item dos serviços que medem o bloco —, e cada bloco desenhado
 * aponta o tipo dele. Como o tipo de estaca (Fase 39), é da obra; ao contrário dele, não tem
 * medidas (F9): o contorno e a altura seguem no desenho de cada bloco.
 *
 * A ordem do fator de um item num bloco (ADR-185): o próprio do card; sem ele, o do tipo; sem
 * ele, o índice da obra. O item que o tipo não tem usa o índice, como na estaca (RN-280).
 */
export interface PileCapType {
  readonly id: PileCapTypeId;
  readonly projectId: ProjectId;
  /** "BL2E" — único entre os tipos vivos da obra. */
  readonly name: string;
  /** A composição: o fator de cada item, pela chave do serviço e a do item. Vazia: segue a obra. */
  readonly rates: readonly ItemFactor[];
  /**
   * A taxa de aço do tipo, kg/m³ por bitola (Fase 41, etapa 2 — RN-543). Vazia ou ausente: o
   * bloco do tipo usa a da disciplina. A lista vale inteira: sem misturar com a da disciplina.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export const PILE_CAP_TYPE_ERROR = {
  NAME_REQUIRED: 'TAKEOFF.PILE_CAP_TYPE_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.PILE_CAP_TYPE_NAME_TAKEN',
  NAME_TOO_LONG: 'TAKEOFF.PILE_CAP_TYPE_NAME_TOO_LONG',
  NOT_FOUND: 'TAKEOFF.PILE_CAP_TYPE_NOT_FOUND',
} as const;

/** "BL3E-PROFUNDO" cabe; um nome de parágrafo não. */
export const PILE_CAP_TYPE_NAME_MAX = 40;

/**
 * O tipo conferido: o nome obrigatório, curto e único na obra, e a composição com um fator
 * positivo por item, com a origem e a foto de cada linha (Fase 42, etapa 4 — `checkTypeLines`,
 * ADR-191: a `MEDIA` sem foto vira `DIGITADO`, a foto fora de forma sai).
 */
export function checkPileCapTypeFields(
  fields: { readonly name: string; readonly rates: readonly ItemFactor[] },
  others: readonly Pick<PileCapType, 'id' | 'name' | 'deletedAt'>[],
  selfId?: PileCapTypeId,
): Result<{ readonly name: string; readonly rates: readonly ItemFactor[] }, DomainError> {
  const nome = fields.name.trim();
  if (nome === '') {
    return err(
      domainError(PILE_CAP_TYPE_ERROR.NAME_REQUIRED, 'O nome do tipo de bloco é obrigatório.'),
    );
  }
  if (nome.length > PILE_CAP_TYPE_NAME_MAX) {
    return err(
      domainError(
        PILE_CAP_TYPE_ERROR.NAME_TOO_LONG,
        `O nome do tipo de bloco tem no máximo ${String(PILE_CAP_TYPE_NAME_MAX)} caracteres.`,
      ),
    );
  }
  const chave = normalizeName(nome);
  if (
    others.some(
      (outro) =>
        outro.deletedAt === undefined && outro.id !== selfId && normalizeName(outro.name) === chave,
    )
  ) {
    return err(
      domainError(PILE_CAP_TYPE_ERROR.NAME_TAKEN, `Já existe o tipo de bloco ${nome} nesta obra.`, {
        name: nome,
      }),
    );
  }
  const composicao = checkTypeLines(fields.rates);
  if (!composicao.ok) return composicao;
  return ok({ name: nome, rates: composicao.value });
}
